/**
 * appointments.service.ts
 *
 * Typed API call wrappers for the appointments domain.
 * All methods unwrap the Axios response and return the typed payload directly.
 */
import { apiClient } from './apiClient';
import type { ApiResponse, PaginatedResponse } from '@/types/api.types';
import {
  toPatientPlanRequirement,
  type PatientPlanRequirement,
} from '@/lib/patientPlanRequirement';

// ─── Domain types (mirrored from server domain types) ─────────────────────────

export type AppointmentStatus =
  | 'scheduled'
  | 'confirmed'
  | 'completed'
  | 'no_show'
  | 'cancelled'
  | 'contact_attempted'
  | 'waiting_list'
  | 'reschedule_pending'
  | 'checked_in'
  | 'checked_back'
  | 'action_required';

/** Legacy `aa_resources.resourceType`. AR-6 adds the `other` bucket so it
 *  stops silently collapsing into `equipment`. */
export type ResourceType = 'doctor' | 'coordinator' | 'nurse' | 'room' | 'equipment' | 'other';

export interface AppointmentResource {
  resourceId: number;
  resourceName: string;
  resourceType: ResourceType;
  /** AR-5/WC-14: `aa_resources.resourceBackgroundColor`. Nullable when
   *  the operator hasn't picked a color. Used for the per-resource dot
   *  strip on the calendar card. */
  resourceColor?: string | null;
}

/**
 * One add-on product attached to an appointment (`appointment_product_addons`,
 * rolled up per product type). Distinct from the products the appointment type
 * *requires* — legacy marks these with an "(Addon)" suffix in the appointment
 * popup (scheduler-appt-info-popup.php 1039-1041).
 */
export interface AppointmentAddonProduct {
  productTypeId: number;
  productName: string;
  quantity: number;
}

export interface Appointment {
  appointmentId: number;
  name: string | null;
  startTime: string;
  endTime: string;
  duration: number | null;
  status: AppointmentStatus;
  patientId: number | null;
  patientName: string | null;
  patientDisplayId: string | null;
  patientDateOfBirth: string | null;
  /** Uncancelled member record on file — drives the "M" badge on the list and
   *  matches what the Membership (member / non-member) filter selects. */
  patientIsMember: boolean;
  // Legacy `eligibleForConfirmation` (scheduler-appt-details.php L380–636)
  // hides "Send Confirmation Now" when either opt-in is false. UAM mirrors
  // that gate. `null` means "no patient attached" (different from "patient
  // attached but opted out"). Reference: docs/audit §1.5 + §5.C.3.
  patientEmailOptIn: boolean | null;
  patientSmsOptIn: boolean | null;
  appointmentTypeId: number | null;
  appointmentTypeName: string | null;
  appointmentTypeColor: string | null;
  appointmentTypeGroupId: number | null;
  appointmentTypeNotes: string | null;
  patientPlanId: number | null;
  planDisplayId: string | null;
  facilityId: number | null;
  facilityName: string | null;
  facilityIanaTimezone: string | null;
  reason: string | null;
  drNotes: string | null;
  staffNotes: string | null;
  patientNotes: string | null;
  confirmed: boolean;
  confirmedDate: string | null;
  confirmedContactMethod: string | null;
  contactAttemptedAt: string | null;
  noShow: boolean;
  waitList: boolean;
  checkedInAt: string | null;
  checkedBackAt: string | null;
  checkedOutAt: string | null;
  cancelledReason: string | null;
  cancelledDate: string | null;
  reschedulePending: boolean;
  /** Manual "first of this appointment type" switch (appointment_addons). */
  firstAppointment: boolean;
  /** Derived server-side: the patient's very first appointment ever. Read-only —
   *  there is no switch for it and it is not part of the write payload. */
  firstVisit: boolean;
  compAppointment: boolean;
  cancelReasonID: number;
  actionRequired: boolean;
  /** Audit-ended (deleted). Only ever true on rows returned by an endpoint that
   *  was explicitly asked for deleted appointments. */
  deleted?: boolean;
  deletedAt?: string | null;
  accountId: number;
  resources: AppointmentResource[];
  /** Only populated by the patient appointments list
   *  (`GET /patients/{id}/appointments`), which batches the rollup; other
   *  appointment endpoints omit it. */
  addonProducts?: AppointmentAddonProduct[];
  isBlock?: boolean;
  recurrence?: {
    recurring: boolean;
    recurringPattern: string | null;
    recurringEndDate: string | null;
    daysOfWeek: string[];
    extraDates: string[];
    exceptionDates: string[];
    required: boolean;
    blockType: string;
  } | null;
}

export interface AppointmentShortcode {
  shortcodeId: number;
  shortcodeText: string;
  shortcodeTitle: string;
  defaultContentText: string;
  contentText: string;
  shortcodeAssignId: number | null;
}

/**
 * A clinical note linked to an appointment. Same key set as the patient note list
 * (so the row renders identically) plus the link metadata.
 *
 * `patientId` is the note's OWNER, which can differ from the appointment's current
 * patient — the appointment's patient may be swapped after links exist. Always use
 * this field for click-through / getNote, never the appointment's patient id.
 */
export interface AppointmentLinkedNote {
  noteId: number;
  patientId: number;
  typeId: number;
  typeName: string;
  title: string;
  noteDate: string | null; // Pacific "YYYY-MM-DD HH:MM:SS"
  date: string | null;
  authorName: string;
  authorId: number;
  active: number; // 0 = soft-deleted note
  visibleToPatient: boolean;
  attachmentCount: number;
  linkId: number;
  linkedDate: string | null;
  linkedByName: string;
  /** The appointment's patient changed after this link was made. */
  patientMismatch: boolean;
}

export type AppointmentProductBucket = 'available' | 'required' | 'disbursed';

export interface AppointmentProductLine {
  scheduledAppointmentProductsId: number;
  appointmentProductAddonsId: number;
  productTypeId: number;
  productTypeName: string;
  quoteItemCostId: number;
  paymentMaterialId: number;
  membershipProductId: number;
  packageProductId: number;
  quantity: number;
  type: string;
  compProduct: boolean;
  isAddon: boolean;
  unitPrice: number;
  nonMemberPrice: number;
  lineTotal: number;
}

export interface AppointmentProductPurchaseOther {
  productTypeId: number;
  productTypeName: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  quoteItemCostId: number;
  scheduledAppointmentProductsIds: number[];
}

export interface AppointmentProductsResponse {
  appointmentId: number | null;
  appointmentTypeId: number;
  isMember: boolean;
  available: AppointmentProductLine[];
  required: AppointmentProductLine[];
  disbursed: AppointmentProductLine[];
  purchaseOthers: AppointmentProductPurchaseOther[];
  totalAmount: number;
  currency: string;
}

export interface AppointmentProductCatalogueItem {
  productTypeId: number;
  productTypeName: string;
  unitPrice: number;
}

export interface AppointmentProductCatalogueResponse {
  isMember: boolean;
  /**
   * Why the patient is or is not a member, which `isMember` alone cannot say.
   *
   * AppointmentProductsService::resolveMembership returns exactly these four:
   * 'active' when a billing record covers today, 'frozen' when the membership
   * or its payment is frozen, 'declined' when the payment was declined, and
   * null when there is no membership or none covering today. The service has
   * always returned it; this interface simply never declared it, so
   * ChargePaymentModal and RecordPatientPaymentModal — which render a banner
   * for 'frozen' and another for 'declined' — were reading an undeclared
   * field.
   */
  memberStatus: 'active' | 'frozen' | 'declined' | null;
  items: AppointmentProductCatalogueItem[];
}

export interface AddAppointmentProductsPayload {
  products: Array<{
    productTypeId: number;
    quantity?: number;
    quoteItemCostId?: number;
    compProduct?: boolean;
    type?: string;
  }>;
}

export interface AppointmentType {
  appointmentTypeId: number;
  appointmentTypeName: string;
  appointmentTypeColor: string | null;
  appointmentTypeFontColor: string | null;
  appointmentTypeDuration: number | null;
  facilityId: number | null;
  maxConflicts: number | null;
  conflictType: string | null;
  /** 0 = nothing, 1 = patient, 2 = patient & plan. */
  requiresPatientPlan: PatientPlanRequirement;
  noConfirm: boolean;
  appointmentTypeDescription: string | null;
  appointmentTypeGroupId: number | null;
  appointmentTypeGroupName: string | null;
  // Legacy `appointment_type_group.groupFacilityID`. `0` means "global"
  // (visible in every facility); any other value scopes the group to a
  // single facility. Drives the modal Group dropdown filter on facility
  // change. Reference: docs/audit/appt-details-parity.md §1.4 + §5.C.4.
  appointmentTypeGroupFacilityId: number;
  appointmentTypeGroupActive: boolean;
  accountId: number;
  /** addon.self_schedule: 0=Off, 1=Bookable, 2=Requestable, 3=Members Only */
  selfSchedule: number;
}

/** Full addon block returned by the API's formatAppointmentTypeResponse. */
export interface AppointmentTypeAddonDetail {
  patient_duration: number | null;
  notes: string;
  self_schedule: number;
  first_time: {
    notes: string;
    duration: number;
    color: string;
    font_color: string;
  };
}

/**
 * Raw appointment type shape from the /appointment-types API.
 * Preserves all relationship data (addon, resources, products, plan types)
 * that the normalized AppointmentType strips away. Used by the admin page.
 */
export interface AppointmentTypeDetail {
  appointmentTypeID: number;
  name: string;
  description: string;
  duration: number;
  color: string;
  font_color: string;
  group_id: number | null;
  group_name: string;
  group_facility_id: number | null;
  group_active: boolean;
  facility_id: number | null;
  max_conflicts: number | null;
  conflict_type: number;
  /** 0 = nothing, 1 = patient, 2 = patient & plan. */
  requires_patient_plan: PatientPlanRequirement;
  no_confirm: boolean;
  order: number;
  active: boolean;
  deleted: boolean;
  accountID: number;
  addon: AppointmentTypeAddonDetail;
  default_fee: string | number | null;
  default_resources: Array<{ resource_id: number; resource_type: string }>;
  required_products: Array<{ product_type_id: number; quantity: number }>;
  /** The one product type this appointment type credits, or null. */
  service_credit_id?: number | null;
  plan_type_ids: number[];
}

/**
 * An appointment type group as GET /appointment-type-groups lists it (the
 * signed-in account only). `order` is atgOrder. The list does NOT carry
 * stage_id — read one group with getAppointmentTypeGroup for that.
 */
export interface AppointmentTypeGroupRow {
  appointmentTypeGroupID: number;
  name: string;
  description: string;
  background_color: string;
  attr_id: number | null;
  confirm_type_id: number | null;
  group_facility_id: number | null;
  order: number;
  active: boolean;
  deleted: boolean;
  appointment_types_count?: number;
  /** Only on the single-group read. */
  stage_id?: number | null;
}

/** A group "Class" option, as GET /appointment-type-group-attrs lists it (global, not per account). */
export interface AppointmentTypeGroupAttr {
  appointmentTypeGroupAttrID: number;
  name: string;
  description?: string;
}

/** Options for POST /appointment-type-groups/{id}/clone. */
export interface CloneAppointmentTypeGroupOptions {
  /** New group name; the API defaults to "Copy of <name>" when blank. */
  name?: string | null;
  /** When > 0, the cloned group and every cloned type get this facility. */
  facility_id?: number | null;
}

/** Fields the group store/update endpoints accept. */
export interface AppointmentTypeGroupPayload {
  name?: string;
  description?: string;
  background_color?: string;
  attr_id?: number | null;
  /**
   * Send the group's current stage on every update: the API writes the
   * baseline stage (1) whenever stage_id is missing.
   */
  stage_id?: number | null;
  confirm_type_id?: number | null;
  group_facility_id?: number | null;
  active?: boolean;
  /** atgOrder. */
  order?: number;
}

/**
 * Fields the appointment-type store/update endpoints accept. On update every
 * key is "only when present" — EXCEPT `addon`, which is written whole (any
 * sub-field left out is reset to its default), and the list fields
 * (resource_ids, required_products, plan_type_ids, service_credit_id), which
 * replace what is there.
 */
export interface AppointmentTypePayload {
  name?: string;
  description?: string;
  duration?: number;
  color?: string;
  font_color?: string;
  /** appointmentTypeGroupID. */
  group_id?: number;
  facility_id?: number;
  active?: boolean;
  max_conflicts?: number;
  conflict_type?: number;
  /** Validated as a boolean by the API — 2 ("Patient & Plan") is refused. */
  requires_patient_plan?: number;
  /** atOrder. */
  order?: number;
  no_confirm?: boolean;
  addon?: {
    patient_duration: number;
    notes: string;
    self_schedule: number;
    first_time: { notes: string; duration: number; color: string; font_color: string };
  };
  default_fee?: number | null;
  resource_ids?: number[];
  required_products?: Array<{ product_type_id: number; quantity: number }>;
  service_credit_id?: number | null;
  plan_type_ids?: number[];
}

/** Fills the optional blocks of a raw appointment type so callers can read them safely. */
function normalizeAppointmentTypeDetail(t: AppointmentTypeDetail): AppointmentTypeDetail {
  return {
    ...t,
    addon: t.addon ?? { patient_duration: null, notes: '', self_schedule: 0, first_time: { notes: '', duration: 0, color: '', font_color: '' } },
    default_resources: Array.isArray(t.default_resources) ? t.default_resources : [],
    required_products: Array.isArray(t.required_products) ? t.required_products : [],
    plan_type_ids: Array.isArray(t.plan_type_ids) ? t.plan_type_ids : [],
  };
}

/**
 * Patient-scoped appointment-type defaults (P7). Mirrors the legacy
 * `/services/appointment-type-details.php` payload that drove
 * `appointment-schedule.js::loadApptDefaultDetails` (L820–L932) and
 * `loadFirstApptDetails` (L2714–L2770). Fields:
 *   - `firstAppointment`  — patient has never had this type before.
 *   - `firstProduct`      — at least one of this type's required product
 *                            types is new for this patient.
 *   - `firstAppointmentTypeDuration` — extra minutes added to the base
 *                            duration when First Appointment is checked.
 *   - `firstAppointmentTypeNotes`    — HTML string shown in the
 *                            First-Time panel when checked.
 *   - `productTypes` — required product type names; the modal applies
 *                            the legacy keyword check ("optims" /
 *                            "tech credit(s)") against this list.
 *
 * Reference: docs/audit/appt-details-parity.md §1.8 + §5.C.12.
 */
export interface AppointmentTypePatientDefaults {
  appointmentTypeId: number;
  appointmentTypeName: string;
  appointmentTypeDuration: number;
  appointmentTypeNotes: string;
  firstAppointment: boolean;
  firstProduct: boolean;
  firstAppointmentTypeDuration: number;
  firstAppointmentTypeNotes: string;
  productTypes: Array<{
    productTypeId: number;
    productTypeName: string;
    quantity: number;
  }>;
  /** Resource IDs from `appointment_resources_defaults` table that should be
   *  auto-populated when this type is selected. Mirrors legacy
   *  `loadApptDetailResourceDefaults`. */
  defaultResourceIds?: number[];
}

export interface Resource {
  resourceId: number;
  resourceName: string;
  resourceType: ResourceType;
  facilityId: number | null;
  facilityIds: number[];
  active: boolean;
  accountId: number;
}

export interface ConflictInfo {
  appointmentId: number;
  patientName: string | null;
  startTime: string;
  endTime: string;
  // The API emits a wider set of conflictType strings (matching legacy
  // SlotAnalyzer + resource-overlap categories):
  //   block_outside_required, block_outside_optional,
  //   block_inside_unavailable_required, block_inside_unavailable_optional,
  //   resource_outside_required, resource_outside_optional,
  //   resource_inside_unavailable_required,
  //   resource_inside_unavailable_optional,
  //   block, resource, provider,
  //   requires_patient, requires_patient_plan, duration
  conflictType: string;
  resourceId?: number;
  resourceName?: string;
  reason?: string;
  severity?: 'error' | 'warning';
  // Capacity hint sent by the API for `conflictType: 'resource'` rows when
  // the overlapping appointment type defines a max-conflicts limit.
  maxConflicts?: number | null;
}

export interface AvailabilitySlot {
  startTime: string;
  endTime: string;
  available: boolean;
  conflicts: ConflictInfo[];
  warnings?: ConflictInfo[];
}

/**
 * Resource summary embedded in an audit revision; mirrors legacy
 * `appointment_resources` rows joined into `scheduler_appt_history.php`.
 */
export interface AppointmentHistoryResource {
  resourceId: number;
  resourceName: string | null;
}

/**
 * One revision of an appointment from the audit log. Field set mirrors
 * every column legacy `scheduler_appt_history.php` (3/31/2026) renders
 * for the "Created" snapshot row and the "Edited" diff rows
 * (Patient, PlanID, Start/End, Duration, Name, Type, Facility, Notes,
 * Confirmed, CheckIn/Back/Out, Cancelled, ContactAttempted, NoShow,
 * WaitList, Resources Added/Removed) plus the auditUser metadata used
 * in each row's heading. Reference: docs/audit/appt-details-parity.md
 * §1.13 + P9.
 */
export interface AppointmentHistoryEntry {
  appointmentId: number;
  auditRecordId: string;
  name: string;
  startTime: string | null;
  endTime: string | null;
  duration: number | null;
  auditStartDate: string | null;
  auditEndDate: string | null;
  auditUserId: number;
  auditUserName: string | null;
  auditUserUsername: string;
  /** Audit-row change reason (e.g. 'Confirmed by patient'). Used to label the actor, never shown verbatim. */
  auditChangeReason: string;
  patientId: number;
  patientName: string | null;
  patientPlanId: number;
  appointmentTypeId: number;
  appointmentTypeName: string | null;
  /** The appointment type group recorded on this revision; 0 when none was stamped. */
  appointmentTypeGroupId: number;
  appointmentTypeGroupName: string | null;
  facilityId: number;
  facilityName: string | null;
  facilityIanaTimezone: string | null;
  reason: string;
  contactAttemptedAt: string | null;
  confirmedDate: string | null;
  checkInAt: string | null;
  checkBackAt: string | null;
  checkOutAt: string | null;
  cancelledAt: string | null;
  cancelledReason: string;
  noShow: boolean;
  waitList: boolean;
  resources: AppointmentHistoryResource[];
  status: AppointmentStatus;
  isBlock: boolean;
}

// ─── Calendar view types ──────────────────────────────────────────────────────

/**
 * Normalized status string from AppointmentPresenterService::status().
 * The canonical precedence legacy applies is: cancelled > no_show >
 * completed > checked_back > waiting_list > confirmed > contact_attempted >
 * scheduled. UI code should prefer `statusKey` over re-deriving from flags.
 */
export type CalendarStatusKey =
  | 'cancelled'
  | 'no_show'
  | 'completed'
  | 'checked_back'
  | 'waiting_list'
  | 'confirmed'
  | 'contact_attempted'
  | 'scheduled';

export interface CalendarAppointment {
  id: number;
  title: string | null;
  start: string;
  end: string;
  patientID: number | null;
  patientName: string | null;
  patientPhone?: string | null;
  patientDisplayId?: string | null;
  /** Uncancelled member record on file — drives the "M" badge in the overview
   *  list. Always false for block rows, which have no patient. */
  patientIsMember?: boolean;
  planId: number | null;
  planDisplayId?: string | null;
  appointmentTypeID: number | null;
  appointmentTypeName: string | null;
  appointmentTypeColor: string | null;
  /** Legacy `appointment_types.appointmentTypeFontColor` — drives cell
   *  text color when there's no comp/first override (scheduler-display-
   *  week-cell.php 850-857). Applied via inline style so it wins over
   *  Tailwind defaults. */
  appointmentTypeFontColor?: string | null;
  /** Legacy `appointment_types.firstAppointmentTypeColor` — used to tint
   *  the cell when `firstAppointment` is true (scheduler-display-week-
   *  cell.php 891-894). Falls back to `appointmentTypeColor` if null. */
  firstAppointmentTypeColor?: string | null;
  /** Legacy `appointment_types_addons.firstAppointmentTypeFontColor` —
   *  text color override for first-time appointments. When unset and
   *  `firstAppointmentTypeColor` is set, the cell falls back to white
   *  text for contrast. */
  firstAppointmentTypeFontColor?: string | null;
  durationMinutes: number | null;
  facilityID: number | null;
  facilityName: string | null;
  resourceID: number | null;
  resourceName: string | null;
  resourceNames?: string[];
  resources: AppointmentResource[];
  confirmed: boolean;
  cancelled: boolean;
  contactAttempted?: boolean;
  contactAttemptedAt?: string | null;
  reschedulePending?: boolean;
  firstAppointment?: boolean;
  compAppointment?: boolean;
  /** Legacy `addonProductsArray[$ID]` — comma-joined `+{N} {productName}`
   *  list of addon products (scheduler-display-week-cell.php 877-886).
   *  Rendered inside the cell italic title parenthetical when present. */
  addonSummary?: string | null;
  noShow: boolean;
  checkInTime: string | null;
  checkBackTime?: string | null;
  checkOutTime: string | null;
  waitList: boolean;
  drNotes: string | null;
  staffNotes: string | null;
  notesSnippet?: string | null;
  /** Legacy per-column quote total feeds off `scheduledAppointmentCancelledReason`
   *  (the legacy schema overloaded that column). Emitted for apptQuoteToggle. */
  quote?: number;
  statusKey?: CalendarStatusKey;
  isBlock?: boolean;
  /**
   * Block-only fields (null on regular appointment rows). Mirror legacy
   * `scheduler-display-week-cell.php` 1113-1124, which renders the
   * Available/Unavailable label and dotpattern/crosshatch background based
   * on `scheduled_appointment_recur.blockType` (1=available, 2=unavailable)
   * and the bold `*` annotation when `required = 1`.
   */
  blockAvailability?: 1 | 2 | null;
  blockType?: 'available' | 'unavailable' | null;
  blockRequired?: boolean | null;
}

// ─── Info popup payloads ──────────────────────────────────────────────────────

/**
 * Custom patient tag rendered in the popup header strip. Mirrors legacy
 * `scheduler-appt-info-popup.php` tag rendering: an icon (often emoji or
 * image URL), a label, and an optional tooltip.
 */
/**
 * Legacy patient medal tiers (scheduler-appt-info-popup.php 768-898).
 * When set on a {@link PopupPatientTag}, the UI renders a colored disc
 * instead of the icon URL. Highest of the three legacy criteria
 * (referrals, lifetime payments, payments by referrals) wins.
 */
export type PopupMedalTier = 'bronze' | 'silver' | 'gold' | 'platinum';

export interface PopupPatientTag {
  id: number;
  label: string;
  iconUrl?: string | null;
  tooltip?: string | null;
  createDate?: string | null;
  addedByName?: string | null;
  /** Optional medal-tier override for legacy referral/spending tiers. */
  medalTier?: PopupMedalTier;
}

/**
 * Scheduled appointment product row (legacy scheduler-appt-info-popup.php
 * 666-728, 1014-1047). One entry per `productTypeID`; the UI renders
 * `+{requiredQuantity} {productName}` and a separate
 * `+{addonQuantity} {productName} (Addon)` line when each count is > 0.
 */
export interface PopupProduct {
  productName: string;
  requiredQuantity: number;
  addonQuantity: number;
  totalQuantity: number;
}

/**
 * One row inside a membership / package card. Quantity is shown as
 * `(X N)` suffix when > 1, suppressed otherwise.
 */
export interface PopupMembershipItem {
  name: string;
  quantity?: number | null;
}

/**
 * Active membership / package card (legacy 1058-1207, 1210-1342).
 * Shape is identical between the two — the renderer just swaps the
 * header icon + "Membership" / "Package" label. `declined` flips the
 * title color to red and prefixes the title with "(Payment Declined)".
 */
export interface PopupMembership {
  title: string;
  declined: boolean;
  procedures: PopupMembershipItem[];
  products: PopupMembershipItem[];
}

/**
 * Resource line in the popup body. Distinct from {@link AppointmentResource}
 * — the popup payload only carries display-relevant fields.
 */
export interface PopupResourceLine {
  resourceId: number;
  name: string;
  type: string;
}

/**
 * Structured payload for the appointment hover popup. Mirrors legacy
 * `scheduler-appt-info-popup.php` so the UI can render each section in the
 * canonical legacy order with proper labels (replacing the prior
 * `Object.entries(popupData)` dump).
 *
 * Each field is conditionally populated server-side based on the
 * `aa_accounts_calendar` `infoXxx` toggles; absent keys mean the account
 * has hidden that row.
 */
export interface AppointmentInfoPopup {
  appointmentId: number;
  startTime: string | null;
  endTime: string | null;
  status: string | null;
  apptType: string | null;
  facility: string | null;
  /** Appointment duration in minutes — drives the popup duration badge. */
  duration?: number | null;
  /** Quote total for this appointment's plan. UI gates display by
   *  `auth.user.userLevel > 2` to mirror legacy `user_level > 2` check. */
  quote?: number | null;
  /** Patient profile photo URL. */
  photoUrl?: string | null;
  dob?: string | null;
  phone?: string | null;
  chartId?: string | null;
  language?: string | null;
  patientName?: string | null;
  patientId?: number | null;
  /** Appointment name (`scheduledAppointmentName`). Hidden by the renderer
   *  when value is empty or literally "Untitled Appointment". */
  name?: string | null;
  resources?: PopupResourceLine[];
  patientNotes?: string | null;
  apptNotes?: string | null;
  planStageId?: number | null;
  /** Display name of the plan stage — e.g. "Initial Consult", "Treatment
   *  In Progress". Looked up from `account_custom_stages` on the API side. */
  planStageName?: string | null;
  planId?: number | null;
  /** Doctor on the plan, when set. Mirrors legacy popup "Doctor: ..." row. */
  doctorName?: string | null;
  /** Coordinator on the plan. Mirrors legacy popup "Coordinator: ..." row. */
  coordinatorName?: string | null;
  /** Procedures from the plan's active quotes — bulleted list. Gated by
   *  `aa_accounts_calendar.infoProcedures`. */
  procedures?: string[];
  /** Custom patient tags. Gated by `aa_accounts_calendar.infoTags`. */
  tags?: PopupPatientTag[];
  /** Minutes since check-in / check-back when in those states. */
  checkedInWaitMinutes?: number | null;
  checkedBackWaitMinutes?: number | null;
  /** Per-appointment scheduled products. Mirrors legacy
   *  "Products (N)" section (scheduler-appt-info-popup.php 666-728). */
  products?: PopupProduct[];
  /** Active memberships for this patient. Empty / undefined when none. */
  memberships?: PopupMembership[];
  /** Active packages for this patient. Empty / undefined when none. */
  packages?: PopupMembership[];
}

/**
 * Structured payload for the block hover popup. Mirrors legacy
 * `scheduler_block_info_popup.php` lines 133-153.
 */
export interface BlockInfoPopup {
  blockId: number;
  name: string | null;
  startTime: string | null;
  endTime: string | null;
  facility: string | null;
  appointmentType: string | null;
  resources: PopupResourceLine[];
  notes: string | null;
  /** True when the block must be honored (plan task validators block on
   *  conflict); false when it's a soft "Optional" block. */
  required: boolean;
  /** True when the block represents *available* time (legacy
   *  `blockType = 1`); false when *unavailable* (`blockType = 2`). */
  available: boolean;
}

// ─── Request payload types ────────────────────────────────────────────────────

export interface AppointmentFilters {
  startDate?: string;
  endDate?: string;
  providerId?: number;
  providerIds?: number[];
  facilityId?: number;
  facilityIds?: number[];
  typeId?: number;
  typeIds?: number[];
  status?: AppointmentStatus;
  statuses?: AppointmentStatus[];
  patientId?: number;
  firstAppointment?: boolean;
  /** Restrict to appointments that are the patient's very first ever. */
  firstVisit?: boolean;
  compAppointment?: boolean;
}

export interface AppointmentPagination {
  page?: number;
  pageSize?: number;
  export?: boolean;
  /**
   * Server-side sort. Maps to the column key declared on the DataTable
   * column (e.g. `startTime`, `patientName`, `appointmentTypeName`,
   * `status`, `facilityName`, `duration`). The API whitelists these keys
   * in `AppointmentsController::index`.
   */
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

/**
 * Fields shared by create and update payloads. Include every legacy-parity
 * field writable from the unified modal (status timestamps, addons,
 * shortcodes). All of these are optional on write.
 */
export interface AppointmentWritableFields {
  /** Map keyed by shortcodeID → contentText, mirrors legacy `shortcodeID{n}` inputs. */
  shortcodes?: Record<string, string>;
  firstAppointment?: boolean;
  compAppointment?: boolean;
  // Per-row status timestamps (nullable to clear; "now" to set to current time)
  contactAttemptedAt?: string | null;
  confirmed?: boolean;
  confirmedAt?: string | null;
  confirmedContactMethod?: string | null;
  checkInAt?: string | null;
  checkBackAt?: string | null;
  checkOutAt?: string | null;
  cancelled?: boolean;
  cancelledAt?: string | null;
  cancelledReason?: string | null;
  cancelReasonID?: number;
  noShow?: boolean;
  /**
   * Required-set switch for this appointment: whether it should carry the
   * appointment type's required products or the single equivalent product.
   * Not stored on the appointment — it only steers which product records get
   * attached server-side.
   */
  product_mode?: "products" | "credit";
}

export interface CreateAppointmentPayload extends AppointmentWritableFields {
  startTime: string;
  endTime: string;
  duration?: number;
  patientId?: number;
  appointmentTypeId?: number;
  patientPlanId?: number;
  facilityId?: number;
  reason?: string;
  drNotes?: string;
  staffNotes?: string;
  waitList?: boolean;
  resourceIds?: number[];
  name?: string;
}

export interface UpdateAppointmentPayload extends AppointmentWritableFields {
  startTime?: string;
  endTime?: string;
  duration?: number;
  patientId?: number;
  appointmentTypeId?: number;
  patientPlanId?: number;
  facilityId?: number;
  reason?: string;
  drNotes?: string;
  staffNotes?: string;
  waitList?: boolean;
  resourceIds?: number[];
  name?: string;
}

export interface UpdateStatusPayload {
  status: AppointmentStatus;
  reason?: string;
  contactMethod?: string;
}

export interface SendConfirmationResult {
  smsSent: boolean;
  emailSent: boolean;
  raw?: string;
}

/**
 * Result of texting the patient that their appointment moved. `sent: false` is
 * a normal outcome (opted out, feature off, no valid number), not an error —
 * the appointment saved either way — so `reason` carries why.
 */
export interface SendBookedSmsUpdateResult {
  sent: boolean;
  reason: string;
  commLogId?: number;
  usedDefault?: boolean;
}

export interface CancelAppointmentPayload {
  reason?: string;
}

function asNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function asBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'yes';
  }

  return false;
}

function normalizeResourceType(value: unknown): ResourceType {
  const normalized = String(value ?? '').trim().toLowerCase();

  switch (normalized) {
    case 'doctors':
    case 'doctor':
      return 'doctor';
    case 'coordinators':
    case 'coordinator':
      return 'coordinator';
    case 'nurses':
    case 'nurse':
      return 'nurse';
    case 'rooms':
    case 'room':
      return 'room';
    case 'equipment':
      return 'equipment';
    case 'other':
      return 'other';
    default:
      // Preserve legacy behaviour for unknown values (empty string / new
      // enum additions) by mapping them to the generic `equipment` bucket.
      return 'equipment';
  }
}

function normalizeAppointmentType(raw: Record<string, unknown>): AppointmentType {
  // The v5 backend (`AppointmentTypesController::formatAppointmentTypeResponse`)
  // returns snake_case keys (`group_id`, `group_name`, `facility_id`, …).
  // We accept legacy camelCase / `*ID` and the nested relation object too so
  // the same shape works regardless of which controller version answers.
  const group = raw.appointmentTypeGroup as Record<string, unknown> | undefined;
  const addon = raw.addon as Record<string, unknown> | undefined;

  return {
    appointmentTypeId: asNumber(raw.appointmentTypeId ?? raw.appointmentTypeID) ?? 0,
    appointmentTypeName: String(raw.appointmentTypeName ?? raw.name ?? ''),
    appointmentTypeColor: raw.appointmentTypeColor == null ? String(raw.color ?? '') || null : String(raw.appointmentTypeColor),
    appointmentTypeFontColor: raw.appointmentTypeFontColor == null ? String(raw.font_color ?? '') || null : String(raw.appointmentTypeFontColor),
    appointmentTypeDuration: asNumber(raw.appointmentTypeDuration ?? raw.duration),
    facilityId: asNumber(raw.facilityId ?? raw.facilityID ?? raw.facility_id),
    maxConflicts: asNumber(raw.maxConflicts ?? raw.max_conflicts),
    conflictType: raw.conflictType == null ? (raw.conflict_type == null ? null : String(raw.conflict_type)) : String(raw.conflictType),
    requiresPatientPlan: toPatientPlanRequirement(raw.requiresPatientPlan ?? raw.requires_patient_plan),
    noConfirm: asBoolean(raw.noConfirm ?? raw.no_confirm),
    appointmentTypeDescription: raw.appointmentTypeDescription == null ? (raw.description == null ? null : String(raw.description)) : String(raw.appointmentTypeDescription),
    appointmentTypeGroupId: asNumber(
      raw.appointmentTypeGroupId
        ?? raw.appointmentTypeGroupID
        ?? raw.group_id
        ?? group?.appointmentTypeGroupId
        ?? group?.appointmentTypeGroupID,
    ),
    appointmentTypeGroupName: raw.appointmentTypeGroupName != null
      ? String(raw.appointmentTypeGroupName)
      : (raw.group_name != null && String(raw.group_name) !== ''
        ? String(raw.group_name)
        : (group?.appointmentTypeGroupName == null ? null : String(group.appointmentTypeGroupName))),
    appointmentTypeGroupFacilityId: asNumber(
      raw.appointmentTypeGroupFacilityId
        ?? raw.group_facility_id
        ?? group?.groupFacilityID
        ?? group?.groupFacilityId,
    ) ?? 0,
    appointmentTypeGroupActive: asBoolean(
      raw.appointmentTypeGroupActive
        ?? raw.group_active
        ?? group?.active
        ?? false,
    ),
    accountId: asNumber(raw.accountId ?? raw.accountID) ?? 0,
    selfSchedule: asNumber(addon?.self_schedule ?? raw.selfSchedule ?? raw.self_schedule) ?? 0,
  };
}

function normalizeResource(raw: Record<string, unknown>): Resource {
  const facilityIds = Array.isArray(raw.facility_ids)
    ? (raw.facility_ids as unknown[]).map((id) => Number(id)).filter((n) => Number.isFinite(n))
    : [];
  return {
    resourceId: asNumber(raw.resourceId ?? raw.resourceID) ?? 0,
    resourceName: String(raw.resourceName ?? raw.name ?? ''),
    resourceType: normalizeResourceType(raw.resourceType ?? raw.resource_type),
    facilityId: asNumber(raw.facilityId ?? raw.facilityID),
    facilityIds,
    active: asBoolean(raw.active ?? raw.available ?? !asBoolean(raw.deleted)),
    accountId: asNumber(raw.accountId ?? raw.accountID) ?? 0,
  };
}

// ─── Cancel reason lookup ────────────────────────────────────────────────────

export interface AppointmentCancelReason {
  appointmentCancelReasonID: number;
  title: string;
  active: boolean;
  rOrder: number;
}

// ─── API wrapper ─────────────────────────────────────────────────────────────

export const appointmentsService = {
  /**
   * Fetch a paginated list of appointments, optionally filtered.
   * Accepts either the unified filtersArray format or legacy per-param format.
   */
  getAppointments(
    filters: AppointmentFilters & { filtersArray?: string } = {},
    pagination: AppointmentPagination = {},
  ) {
    let params: Record<string, string | number | boolean | undefined>;

    if (filters.filtersArray) {
      params = {
        filtersArray:  filters.filtersArray,
        search:        undefined,
        page:          pagination.page,
        perPage:       pagination.pageSize,
        export:        pagination.export,
        sortBy:        pagination.sortBy,
        sortDir:       pagination.sortDir,
      };
    } else {
      params = {
        dateFrom:           filters.startDate,
        dateTo:             filters.endDate,
        providerId:         filters.providerId,
        providerIds:        filters.providerIds?.join(','),
        facilityId:         filters.facilityId,
        facilityIds:        filters.facilityIds?.join(','),
        appointmentTypeId:  filters.typeId,
        appointmentTypeIds: filters.typeIds?.join(','),
        status:             filters.status,
        statuses:           filters.statuses?.join(','),
        patientId:          filters.patientId,
        firstAppointment:   filters.firstAppointment ? '1' : undefined,
        firstVisit:         filters.firstVisit ? '1' : undefined,
        compAppointment:    filters.compAppointment ? '1' : undefined,
        page:               pagination.page,
        perPage:            pagination.pageSize,
        export:             pagination.export,
        sortBy:             pagination.sortBy,
        sortDir:            pagination.sortDir,
      };
    }

    // Strip undefined so Axios doesn't serialise them as empty params
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined),
    );

    return apiClient
      .get<PaginatedResponse<Appointment>>('/appointments', { params: cleanParams })
      .then((r) => r.data);
  },

  /**
   * Fetch a single appointment by ID.
   */
  getAppointment(id: number) {
    return apiClient
      .get<ApiResponse<Appointment>>(`/appointments/${id}`)
      .then((r) => r.data.data);
  },

  /**
   * Create a new appointment.
   */
  createAppointment(data: CreateAppointmentPayload) {
    return apiClient
      .post<ApiResponse<Appointment>>('/appointments', data)
      .then((r) => ({ data: r.data.data, meta: r.data.meta }));
  },

  /**
   * Update an existing appointment (full replace of provided fields).
   */
  updateAppointment(id: number, data: UpdateAppointmentPayload) {
    return apiClient
      .put<ApiResponse<Appointment>>(`/appointments/${id}`, data)
      .then((r) => ({ data: r.data.data, meta: r.data.meta }));
  },

  /**
   * Transition an appointment's status.
   */
  updateStatus(id: number, payload: UpdateStatusPayload) {
    return apiClient
      .patch<ApiResponse<Appointment>>(`/appointments/${id}/status`, payload)
      .then((r) => r.data.data);
  },

  /**
   * Soft-cancel an appointment with an optional reason.
   */
  cancelAppointment(id: number, reason?: string) {
    const payload: CancelAppointmentPayload = reason ? { reason } : {};
    return apiClient
      .delete<void>(`/appointments/${id}`, { data: payload })
      .then(() => undefined);
  },

  /**
   * Check availability for a given appointment type, provider, and date.
   * Returns time slots for the day with conflict information.
   *
   * Legacy parity (`appointment-schedule.js` `checkForBlocks` L7935–L7944):
   * the modal forwards both the attached patient and plan into the conflict
   * strip so the back-end `SlotAnalyzer` can resolve `requiresPatientPlan`
   * (1 = patient required, 2 = plan required) against the correct context.
   * Without these, the analyzer sees `patientID === 0` and emits
   * "Appointment type requires a patient" even when one is attached.
   * Reference: docs/audit/appt-details-parity.md §5.B.
   */
  checkAvailability(options: {
    typeId?: number;
    appointmentTypeGroupId?: number;
    resourceId?: number;
    providerId?: number;
    resourceIds?: number[];
    date?: string;
    dateFrom?: string;
    dateTo?: string;
    duration?: number;
    facilityId?: number;
    startTime?: string;
    timeStart?: string;
    timeEnd?: string;
    includeWeekends?: boolean;
    appointmentId?: number;
    patientId?: number;
    planId?: number;
    blocksOnly?: boolean;
  } = {}) {
    const params: Record<string, string | number | number[] | undefined> = {
      typeId: options.typeId,
      appointmentTypeGroupId: options.appointmentTypeGroupId,
      resourceId: options.resourceId ?? options.providerId,
      providerId: options.providerId,
      resourceIds: options.resourceIds,
      date: options.date,
      dateFrom: options.dateFrom,
      dateTo: options.dateTo,
      duration: options.duration,
      facilityId: options.facilityId,
      startTime: options.startTime,
      timeStart: options.timeStart,
      timeEnd: options.timeEnd,
      includeWeekends:
        options.includeWeekends === undefined ? undefined : options.includeWeekends ? 1 : 0,
      appointmentId: options.appointmentId,
      patientId: options.patientId,
      planId: options.planId,
      blocksOnly: options.blocksOnly ? 1 : undefined,
    };

    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined),
    );

    return apiClient
      .get<ApiResponse<AvailabilitySlot[]>>('/appointments/availability', { params: cleanParams })
      .then((r) => r.data.data);
  },

  /**
   * Preview whether moving / resizing an appointment would create conflicts.
   * Legacy `scheduler_check_blocks.php?checkSavedAppt=1` parity.
   */
  dragCheck(payload: {
    appointmentId: number;
    newStart: string;
    newEnd: string;
    resourceIds?: number[];
    facilityId?: number;
    appointmentTypeId?: number;
    appointmentTypeGroupId?: number;
    // Required for new appointments (no `findAppointment` fallback to
    // pull patient/plan from the persisted row). Legacy
    // `services/calendar.php` `checkForBlocks` forwarded both
    // (appointment-schedule.js L7942-L7943) so SlotAnalyzer can resolve
    // `requiresPatientPlan` against the actual attached context.
    patientId?: number;
    planId?: number;
  }) {
    return apiClient
      .post<ApiResponse<{
        available: boolean;
        canSaveAnyway: boolean;
        conflicts: ConflictInfo[];
        warnings: ConflictInfo[];
      }>>('/appointments/availability/drag-check', payload)
      .then((r) => r.data.data);
  },

  /**
   * Compute a plan-stage recommendation for this appointment (legacy
   * `scheduler_check_stage.php`).
   */
  stageRecommendation(id: number, params: {
    triggerAction?: string;
    changedCheckIn?: boolean;
    changedCheckOut?: boolean;
    changedCancelled?: boolean;
    changedNoShow?: boolean;
  } = {}) {
    const query: Record<string, string | number> = {};
    if (params.triggerAction) query.triggerAction = params.triggerAction;
    if (params.changedCheckIn) query.changedCheckIn = 1;
    if (params.changedCheckOut) query.changedCheckOut = 1;
    if (params.changedCancelled) query.changedCancelled = 1;
    if (params.changedNoShow) query.changedNoShow = 1;

    return apiClient
      .get<ApiResponse<{ recommendation: null | {
        currentStageId: number;
        targetStageId: number;
        targetStageName: string | null;
        triggerAction: string;
      } }>>(`/appointments/${id}/stage-recommendation`, { params: query })
      .then((r) => r.data.data.recommendation);
  },

  /** Apply a plan-stage change after user confirmation. */
  stageApply(id: number, targetStageId: number) {
    return apiClient
      .post<ApiResponse<{ planId: number; previousStageId: number; newStageId: number; eventId: number | null }>>(
        `/appointments/${id}/stage-apply`,
        { targetStageId },
      )
      .then((r) => r.data.data);
  },

  /** Rich hover-popup payload for an appointment. See
   *  {@link AppointmentInfoPopup} — fields are conditionally populated by
   *  the server based on `aa_accounts_calendar` flags. */
  getAppointmentInfoPopup(id: number): Promise<AppointmentInfoPopup> {
    return apiClient
      .get<ApiResponse<AppointmentInfoPopup>>(`/appointments/${id}/info-popup`)
      .then((r) => r.data.data);
  },

  /** Dispatch a mobile push notification for this appointment. */
  mobilePush(id: number) {
    return apiClient
      .post<ApiResponse<{ queued: boolean; appointmentId: number }>>(`/appointments/${id}/mobile-push`)
      .then((r) => r.data.data);
  },

  /**
   * Send an appointment confirmation now (legacy "Send confirmation now").
   * Proxies to the byte-identical legacy appt_confirm_now.php: the channel
   * (SMS / email / both) is chosen by the appointment's confirm type, not by
   * the caller. This ONLY sends + logs; it does not change confirmed status.
   */
  sendConfirmation(id: number) {
    return apiClient
      .post<ApiResponse<SendConfirmationResult>>(`/appointments/${id}/confirm`)
      .then((r) => r.data.data);
  },

  /**
   * Tell the patient their appointment has MOVED, using the practice's booking
   * wording rendered against the new date/time and prefixed with "Updated: ".
   *
   * Only called on an explicit yes to the prompt raised after a save that
   * changed the start time (meta.workflow.promptBookedSmsUpdate).
   */
  sendBookedSmsUpdate(id: number) {
    return apiClient
      .post<ApiResponse<SendBookedSmsUpdateResult>>(`/appointments/${id}/booked-sms-update`)
      .then((r) => r.data.data);
  },

  /**
   * Record a contact attempt against the appointment (sets attempt date only).
   */
  contactAttempted(id: number) {
    return apiClient
      .post<ApiResponse<Appointment>>(`/appointments/${id}/contact-attempted`)
      .then((r) => r.data.data);
  },

  /**
   * Clone an existing appointment (legacy `convertApptToAppt`).
   */
  copyAppointment(id: number) {
    return apiClient
      .post<ApiResponse<Appointment>>(`/appointments/${id}/copy`)
      .then((r) => r.data.data);
  },

  /**
   * Hard (soft) delete — sets `auditEndDate`. Distinct from cancel.
   * Matches legacy `delete_appt.php`.
   */
  hardDeleteAppointment(id: number) {
    return apiClient
      .delete<ApiResponse<{ deleted: boolean; appointmentId: number }>>(`/appointments/${id}/hard`)
      .then((r) => r.data.data);
  },

  /**
   * List shortcodes for an appointment (master list + per-appointment values).
   */
  getAppointmentShortcodes(id: number) {
    return apiClient
      .get<ApiResponse<AppointmentShortcode[]>>(`/appointments/${id}/shortcodes`)
      .then((r) => r.data.data);
  },

  /**
   * Clinical notes linked to an appointment.
   *
   * `id` may be a stale PK — appointments are audit-versioned and any other user's
   * save mints a new one — so the server resolves it through the audit chain to the
   * live row before reading the links. Kept thin on purpose: if the URI or body
   * keys move, only these three methods change.
   */
  getAppointmentNotes(id: number) {
    return apiClient
      .get<ApiResponse<AppointmentLinkedNote[]>>(`/appointments/${id}/notes`)
      .then((r) => r.data.data);
  },

  /** Link an existing note to an appointment. Idempotent. */
  linkAppointmentNote(id: number, noteId: number) {
    return apiClient
      .post<ApiResponse<{
        linkId: number;
        appointmentId: number;
        noteId: number;
        linked: boolean;
        alreadyLinked: boolean;
      }>>(`/appointments/${id}/notes`, { noteId })
      .then((r) => r.data.data);
  },

  /** Unlink a note from an appointment. Idempotent — `unlinked` is 0 if it wasn't linked. */
  unlinkAppointmentNote(id: number, noteId: number) {
    return apiClient
      .delete<ApiResponse<{ appointmentId: number; noteId: number; unlinked: number }>>(
        `/appointments/${id}/notes/${noteId}`,
      )
      .then((r) => r.data.data);
  },

  /**
   * Get required/optional products and pricing for an appointment.
   *
   * P6 — `apptComp` mirrors the legacy `loadApptProductDefaults` payload key:
   * the live state of the modal's `compAppointment` checkbox can be forwarded
   * so the products grid reflects comp pricing without forcing the user to
   * save first. When omitted the backend falls back to the persisted
   * `appointment_addons.compAppointment` row.
   */
  getAppointmentProducts(id: number, options?: { apptComp?: boolean }) {
    const params: Record<string, string | number> = {};
    if (options?.apptComp !== undefined) {
      params.apptComp = options.apptComp ? 1 : 0;
    }
    return apiClient
      .get<ApiResponse<AppointmentProductsResponse>>(`/appointments/${id}/products`, {
        params: Object.keys(params).length > 0 ? params : undefined,
      })
      .then((r) => r.data.data);
  },

  /**
   * Attach required products to an appointment.
   */
  addAppointmentProducts(id: number, payload: AddAppointmentProductsPayload) {
    return apiClient
      .post<ApiResponse<{ inserted: number[] }>>(`/appointments/${id}/products`, payload)
      .then((r) => r.data.data);
  },

  /**
   * Preview required-products buckets for an appointment that has not been
   * saved yet. Mirrors the "fresh appointment" branch of
   * `appointment-product-types.php`.
   *
   * P6 — `apptComp` is the live state of the modal's `compAppointment`
   * checkbox. When `true`, the backend promotes every pending slot into the
   * disbursed bucket with `type='comp'` so the preview reflects the modal's
   * choice without requiring a save.
   */
  previewAppointmentProducts(
    appointmentTypeId: number,
    patientId?: number | null,
    options?: { apptComp?: boolean },
  ) {
    return apiClient
      .post<ApiResponse<AppointmentProductsResponse>>('/appointments/products/preview', {
        appointmentTypeId,
        patientId: patientId ?? null,
        apptComp: options?.apptComp ?? false,
      })
      .then((r) => r.data.data);
  },

  /**
   * Catalogue of sellable product types — used by the Add Products modal.
   */
  getAppointmentProductCatalogue(facilityId?: number | null, patientId?: number | null) {
    const params: Record<string, number> = {};
    if (facilityId) params.facilityId = facilityId;
    if (patientId) params.patientId = patientId;
    return apiClient
      .get<ApiResponse<AppointmentProductCatalogueResponse>>('/appointments/products/catalogue', {
        params: Object.keys(params).length ? params : undefined,
      })
      .then((r) => r.data.data);
  },

  /**
   * Mark a slot as disbursed against the patient's existing inventory or the
   * payment record carried in the row.
   */
  disburseAppointmentProduct(appointmentId: number, scheduledAppointmentProductsId: number) {
    return apiClient
      .post<ApiResponse<AppointmentProductsResponse>>(
        `/appointments/${appointmentId}/products/${scheduledAppointmentProductsId}/disburse`
      )
      .then((r) => r.data.data);
  },

  /**
   * Toggle the `compProduct` flag on a slot.
   */
  compAppointmentProduct(appointmentId: number, scheduledAppointmentProductsId: number, comp: boolean) {
    return apiClient
      .post<ApiResponse<AppointmentProductsResponse>>(
        `/appointments/${appointmentId}/products/${scheduledAppointmentProductsId}/comp`,
        { comp }
      )
      .then((r) => r.data.data);
  },

  /**
   * Reverse a disbursement; returns the consumed material to the patient's
   * inventory and re-creates the matching pending slot.
   */
  returnAppointmentProduct(appointmentId: number, scheduledAppointmentProductsId: number) {
    return apiClient
      .post<ApiResponse<AppointmentProductsResponse>>(
        `/appointments/${appointmentId}/products/${scheduledAppointmentProductsId}/return`
      )
      .then((r) => r.data.data);
  },

  /**
   * Remove an addon line from the appointment. The corresponding addon-quantity
   * is decremented; the SAP row is soft-deleted.
   */
  removeAppointmentProduct(appointmentId: number, scheduledAppointmentProductsId: number) {
    return apiClient
      .delete<ApiResponse<AppointmentProductsResponse>>(
        `/appointments/${appointmentId}/products/${scheduledAppointmentProductsId}`
      )
      .then((r) => r.data.data);
  },

  /**
   * Load a patient into an existing appointment without running the full save.
   */
  setAppointmentPatient(id: number, patientId: number, patientPlanId?: number | null) {
    const body: Record<string, unknown> = { patientId };
    if (patientPlanId !== undefined) body.patientPlanId = patientPlanId;
    return apiClient
      .post<ApiResponse<Appointment>>(`/appointments/${id}/patient`, body)
      .then((r) => r.data.data);
  },

  /**
   * Unload the patient (and attached plan) from an existing appointment.
   */
  clearAppointmentPatient(id: number) {
    return apiClient
      .delete<ApiResponse<Appointment>>(`/appointments/${id}/patient`)
      .then((r) => r.data.data);
  },

  /**
   * Fetch all appointment types for the current account.
   */
  getAppointmentTypes() {
    return apiClient
      .get<ApiResponse<Record<string, unknown>[]>>('/appointment-types', {
        params: { active: true },
      })
      .then((r) => (r.data.data ?? []).map((row) => normalizeAppointmentType(row)));
  },

  /**
   * Fetch all appointment types (active only) with full raw data for admin.
   * Returns the raw API shape with addon, resources, products, etc.
   * includeDeleted drops the `active` filter: the API then returns active
   * and soft-deleted types together (`active` false on a deleted one).
   */
  listAllAppointmentTypesRaw(options: { includeDeleted?: boolean } = {}) {
    return apiClient
      .get<ApiResponse<AppointmentTypeDetail[]>>('/appointment-types', {
        params: options.includeDeleted ? {} : { active: true },
      })
      .then((r) => (r.data.data ?? []).map(normalizeAppointmentTypeDetail));
  },

  /** Create an appointment type in the signed-in account. Returns the raw shape. */
  createAppointmentType(payload: AppointmentTypePayload) {
    return apiClient
      .post<ApiResponse<AppointmentTypeDetail>>('/appointment-types', payload)
      .then((r) => normalizeAppointmentTypeDetail(r.data.data));
  },

  /**
   * Update an appointment type. Keys left out are kept, except that `addon`
   * is written whole — send every addon field (see AppointmentTypePayload).
   */
  updateAppointmentType(id: number, payload: AppointmentTypePayload) {
    return apiClient
      .put<ApiResponse<AppointmentTypeDetail>>(`/appointment-types/${id}`, payload)
      .then((r) => normalizeAppointmentTypeDetail(r.data.data));
  },

  /** Soft delete (typeActive = 0). */
  async deleteAppointmentType(id: number): Promise<void> {
    await apiClient.delete(`/appointment-types/${id}`);
  },

  async restoreAppointmentType(id: number): Promise<void> {
    await apiClient.post(`/appointment-types/${id}/restore`);
  },

  /**
   * Save one group's types in their dropped order. `typeIds` is the group's
   * FULL list after the drop; any type that came from another group is moved
   * into this one (writes atOrder 1..n and appointmentTypeGroupID).
   */
  async reorderAppointmentTypes(groupId: number, typeIds: number[]): Promise<void> {
    await apiClient.post('/appointment-types/reorder', { groupId, typeIds });
  },

  /**
   * Active appointment type groups of the signed-in account, in atgOrder
   * order. `{ active: false }` returns ONLY the soft-deleted groups (the API
   * has no "both" value).
   */
  listAppointmentTypeGroups(options: { active?: boolean } = {}) {
    return apiClient
      .get<ApiResponse<AppointmentTypeGroupRow[]>>(
        '/appointment-type-groups',
        options.active === false ? { params: { active: 0 } } : undefined,
      )
      .then((r) => (Array.isArray(r.data.data) ? r.data.data : []));
  },

  /** One group, including its stage_id (which the list leaves out). */
  getAppointmentTypeGroup(id: number) {
    return apiClient
      .get<ApiResponse<AppointmentTypeGroupRow>>(`/appointment-type-groups/${id}`)
      .then((r) => r.data.data);
  },

  createAppointmentTypeGroup(payload: AppointmentTypeGroupPayload) {
    return apiClient
      .post<ApiResponse<AppointmentTypeGroupRow>>('/appointment-type-groups', payload)
      .then((r) => r.data.data);
  },

  /** Keys left out are kept — except stage_id, which the API resets to 1 when missing. */
  updateAppointmentTypeGroup(id: number, payload: AppointmentTypeGroupPayload) {
    return apiClient
      .put<ApiResponse<AppointmentTypeGroupRow>>(`/appointment-type-groups/${id}`, payload)
      .then((r) => r.data.data);
  },

  /**
   * Soft delete (active = 0). The group's types are NOT touched: they keep
   * pointing at the deleted group until moved.
   */
  async deleteAppointmentTypeGroup(id: number): Promise<void> {
    await apiClient.delete(`/appointment-type-groups/${id}`);
  },

  async restoreAppointmentTypeGroup(id: number): Promise<void> {
    await apiClient.post(`/appointment-type-groups/${id}/restore`);
  },

  /** Save the full group order (writes atgOrder 1..n). */
  async reorderAppointmentTypeGroups(groupIds: number[]): Promise<void> {
    await apiClient.post('/appointment-type-groups/reorder', { groupIds });
  },

  /**
   * Copy a group and all its ACTIVE appointment types (resources, current fee,
   * required products, addon, service credit, plan types, images) in the same
   * account. A name used by a live group is refused with 422 DUPLICATE_NAME.
   * The answer is the new group; `appointment_types_count` is how many types
   * were copied.
   */
  cloneAppointmentTypeGroup(id: number, options: CloneAppointmentTypeGroupOptions = {}) {
    const body: Record<string, unknown> = {};
    if (options.name && options.name.trim() !== '') body.name = options.name.trim();
    if (options.facility_id && options.facility_id > 0) body.facility_id = options.facility_id;
    return apiClient
      .post<ApiResponse<AppointmentTypeGroupRow>>(`/appointment-type-groups/${id}/clone`, body)
      .then((r) => r.data.data);
  },

  /** Group "Class" options (GET /appointment-type-group-attrs). */
  listAppointmentTypeGroupAttrs() {
    return apiClient
      .get<ApiResponse<AppointmentTypeGroupAttr[]>>('/appointment-type-group-attrs')
      .then((r) => (Array.isArray(r.data.data) ? r.data.data : []));
  },

  /**
   * Fetch appointment-type images. Returns a map of appointmentTypeID -> imageUrl.
   */
  async getAppointmentTypeImages(): Promise<Record<string, string>> {
    const res = await apiClient.get<ApiResponse<Array<{
      appointmentTypeID?: number;
      productUrl?: string;
      defaultUrl?: number | string;
    }>>>('/product-image-urls', { params: { type: 'appointment-type' } });
    const images = res.data.data ?? [];
    const map: Record<string, string> = {};
    for (const img of images) {
      const atId = String(img.appointmentTypeID ?? '');
      if (atId && img.productUrl) {
        if (!map[atId] || img.defaultUrl === 1 || img.defaultUrl === '1') {
          map[atId] = img.productUrl;
        }
      }
    }
    return map;
  },

  /**
   * Fetch lookup maps for resources, product types, and plan types.
   * Uses the same raw APIs as the corporate dashboard.
   */
  async getApptTypeLookups(): Promise<{
    resourceMap: Record<string, string>;
    /** Resource id → its raw resource type ('doctors', 'Doctor', 'room', …), where the API sends one. */
    resourceTypeMap: Record<string, string>;
    productTypeMap: Record<string, string>;
    /**
     * Product type ids offered as Required Products and as Service Credit, by
     * the corp-dashboard rule: live (status ≠ 0) and not Payment Disbursal
     * (disburseType ≠ 1); Required Products also need an active product.
     * Absent when the API sends none of those fields — then every type is offered.
     */
    requiredProductTypeIds?: string[];
    serviceCreditTypeIds?: string[];
    planTypeMap: Record<string, string>;
  }> {
    const [resRes, ptRes, planRes] = await Promise.all([
      apiClient.get<ApiResponse<Record<string, unknown>[]>>('/resources').catch(() => ({ data: { data: [] } })),
      apiClient.get<ApiResponse<Record<string, unknown>[]>>('/product-types').catch(() => ({ data: { data: [] } })),
      apiClient.get<ApiResponse<Record<string, unknown>[]>>('/plan-types').catch(() => ({ data: { data: [] } })),
    ]);

    const resourceMap: Record<string, string> = {};
    const resourceTypeMap: Record<string, string> = {};
    for (const r of (resRes.data?.data ?? [])) {
      const id = String(r.resourceID ?? r.resourceId ?? r.id ?? '');
      const name = String(r.name ?? r.resourceName ?? '');
      if (id && name) resourceMap[id] = name;
      const type = String(r.resource_type ?? r.resourceType ?? '').trim();
      if (id && type) resourceTypeMap[id] = type;
    }

    const productTypeMap: Record<string, string> = {};
    const requiredProductTypeIds: string[] = [];
    const serviceCreditTypeIds: string[] = [];
    for (const pt of (ptRes.data?.data ?? [])) {
      const id = String(pt.productTypeID ?? pt.productTypeId ?? pt.id ?? '');
      const name = String(pt.productTypeName ?? pt.name ?? '');
      if (!id || !name) continue;
      productTypeMap[id] = name;
      const live = pt.status === undefined || Number(pt.status) !== 0;
      const retail = Number(pt.disburseType) === 1;
      if (!live || retail) continue;
      serviceCreditTypeIds.push(id);
      if (pt.activeProductsCount === undefined || Number(pt.activeProductsCount) > 0) requiredProductTypeIds.push(id);
    }

    const planTypeMap: Record<string, string> = {};
    for (const p of (planRes.data?.data ?? [])) {
      const id = String(p.planTypeID ?? p.planTypeId ?? p.id ?? '');
      const name = String(p.name ?? '');
      if (id && name) planTypeMap[id] = name;
    }

    return { resourceMap, resourceTypeMap, productTypeMap, requiredProductTypeIds, serviceCreditTypeIds, planTypeMap };
  },

  /**
   * Change self_schedule on an appointment type's addon. The API writes the
   * addon whole (any sub-field left out is reset), so pass the type's current
   * addon and only self_schedule changes.
   */
  updateAppointmentTypeSelfSchedule(id: number, selfSchedule: number, currentAddon?: AppointmentTypeAddonDetail) {
    const addon = currentAddon
      ? {
          patient_duration: currentAddon.patient_duration ?? 0,
          notes: currentAddon.notes ?? '',
          self_schedule: selfSchedule,
          first_time: {
            notes: currentAddon.first_time?.notes ?? '',
            duration: currentAddon.first_time?.duration ?? 0,
            color: currentAddon.first_time?.color ?? '',
            font_color: currentAddon.first_time?.font_color ?? '',
          },
        }
      : { self_schedule: selfSchedule };
    return apiClient
      .put<ApiResponse<Record<string, unknown>>>(`/appointment-types/${id}`, { addon })
      .then((r) => normalizeAppointmentType(r.data.data as Record<string, unknown>));
  },

  /**
   * Fetch patient-scoped defaults for an appointment type. Drives the
   * "First Appointment" auto-check + first-time panel exactly the same
   * way as legacy `appointment-schedule.js::loadApptDefaultDetails`
   * (L820–L932). Pass `patientId` as `null`/omit it for new
   * appointments without a patient yet — the server returns the same
   * payload but with `firstAppointment` / `firstProduct` left at
   * `false` (legacy `appointment_types_db.php` L501 short-circuits the
   * patient query when `patientID == ""`).
   *
   * Reference: docs/audit/appt-details-parity.md §1.8 + §5.C.12.
   */
  getAppointmentTypePatientDefaults(appointmentTypeId: number, patientId?: number | null) {
    return apiClient
      .get<ApiResponse<AppointmentTypePatientDefaults>>(
        `/appointment-types/${appointmentTypeId}/patient-defaults`,
        {
          params:
            patientId !== null && patientId !== undefined && patientId > 0
              ? { patientId }
              : undefined,
        },
      )
      .then((r) => r.data.data);
  },

  /**
   * Fetch all schedulable resources (staff, rooms, equipment).
   * Optionally filter by facility.
   */
  getResources(facilityId?: number) {
    const params = facilityId !== undefined ? { facilityId } : {};
    return apiClient
      .get<ApiResponse<Record<string, unknown>[]>>('/resources', { params })
      .then((r) => (r.data.data ?? []).map((row) => normalizeResource(row)));
  },

  /**
   * Fetch soft-deleted appointments (paginated).
   */
  listDeleted(pagination: AppointmentPagination = {}) {
    const params: Record<string, string | number | undefined> = {
      page: pagination.page,
      perPage: pagination.pageSize,
    };
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined),
    );
    return apiClient
      .get<PaginatedResponse<Appointment>>('/appointments/deleted', { params: cleanParams })
      .then((r) => r.data);
  },

  /**
   * Restore a soft-deleted appointment.
   */
  restoreAppointment(id: number) {
    return apiClient
      .post<ApiResponse<Appointment>>(`/appointments/${id}/restore`)
      .then((r) => r.data.data);
  },

  getAppointmentHistory(id: number) {
    return apiClient
      .get<ApiResponse<AppointmentHistoryEntry[]>>(`/appointments/${id}/history`)
      .then((r) => r.data.data);
  },

  listAppointmentCancelReasons() {
    return apiClient
      .get<ApiResponse<AppointmentCancelReason[]>>('/appointment-cancel-reasons')
      .then((r) => r.data.data ?? []);
  },
};

// ─── Calendar stand-alone helpers ─────────────────────────────────────────────

/**
 * Fetch all appointments within [startDate, endDate) for the calendar grid.
 * Dates should be ISO date strings (YYYY-MM-DD or full ISO datetime).
 */
export function getCalendarAppointments(
  startDate: string,
  endDate: string,
  facilityId?: number,
  options?: {
    includeCancelled?: boolean;
    appointmentTypeId?: number;
    /**
     * Legacy dual-axis: resource IDs that drive grid columns. OR-matched — any
     * appointment touching one of these resources is returned. Maps to legacy
     * `subFiltersPeople[]` (calendar-test.php 7329-7374).
     */
    scheduleResourceIds?: number[];
    /**
     * Legacy dual-axis: secondary AND-filter — appointment must touch ALL
     * resources in this list. Maps to legacy `subFiltersResources[]`
     * (calendar-test.php 7735-7771, returnFilterString 1655-1788).
     */
    filterResourceIds?: number[];
  },
): Promise<CalendarAppointment[]> {
  const params: Record<string, string | number> = { startDate, endDate };
  if (facilityId !== undefined) params.facilityId = facilityId;
  if (options?.includeCancelled) params.includeCancelled = '1';
  if (options?.appointmentTypeId) params.appointmentTypeId = options.appointmentTypeId;
  if (options?.scheduleResourceIds?.length) {
    params.scheduleResourceIds = options.scheduleResourceIds.join(',');
  }
  if (options?.filterResourceIds?.length) {
    params.filterResourceIds = options.filterResourceIds.join(',');
  }

  return apiClient
    .get<ApiResponse<CalendarAppointment[]>>('/appointments/calendar', { params })
    .then((r) => r.data.data);
}

/**
 * Fetch the calendar Overview's "Waiting List" panel.
 *
 * Deliberately NOT day-scoped: this returns every wait-list entry whose start
 * time is still in the future, so the panel keeps showing a patient who wants
 * an earlier slot no matter which day the calendar is on, and drops them once
 * their appointment passes. Legacy parity with `showWaitList = '1'`
 * (scheduler_display_tz.php 1121-1122).
 */
export function getWaitlistAppointments(
  facilityId?: number,
  options?: {
    appointmentTypeId?: number;
    scheduleResourceIds?: number[];
  },
): Promise<CalendarAppointment[]> {
  const params: Record<string, string | number> = {};
  if (facilityId !== undefined) params.facilityId = facilityId;
  if (options?.appointmentTypeId) params.appointmentTypeId = options.appointmentTypeId;
  if (options?.scheduleResourceIds?.length) {
    params.scheduleResourceIds = options.scheduleResourceIds.join(',');
  }

  return apiClient
    .get<ApiResponse<{ appointments: CalendarAppointment[]; total: number }>>(
      '/scheduler/waitlist',
      { params },
    )
    .then((r) => r.data.data.appointments ?? []);
}

/** Record a check-in timestamp for an appointment. */
export function checkInAppointment(id: number): Promise<void> {
  return apiClient.post(`/appointments/${id}/checkin`).then(() => undefined);
}

/** Record a check-out timestamp for an appointment. */
export function checkOutAppointment(id: number): Promise<void> {
  return apiClient.post(`/appointments/${id}/checkout`).then(() => undefined);
}

/** Mark appointment as confirmed (legacy progressAppointment('confirm')). */
export function confirmAppointment(id: number): Promise<void> {
  return apiClient.post(`/appointments/${id}/confirm`).then(() => undefined);
}

/** Mark appointment as checked-back — sets scheduledAppointmentStatusCheckBackTime.
 *  Legacy progressAppointment('checkback'). Routed through PATCH /appointments/{id}/status
 *  with status=checked_back (workflow service handles the timestamp). */
export function checkBackAppointment(id: number): Promise<void> {
  return apiClient
    .patch(`/appointments/${id}/status`, { status: 'checked_back' })
    .then(() => undefined);
}

/** Fetch all non-cancelled appointments for a specific plan (used in PlanDetailPage). */
export function getAppointmentsByPlan(planId: number): Promise<Appointment[]> {
  return apiClient
    .get<PaginatedResponse<Appointment>>('/appointments', { params: { planId, perPage: 100 } })
    .then((r) => r.data.data);
}

/**
 * Fetch a patient's appointments (includes cancelled and no-show).
 *
 * Deleted appointments are excluded unless `includeDeleted` is set, in which
 * case they come back flagged `deleted: true`. They are read-only — the detail
 * endpoint resolves to the live row of an audit chain and 404s on a deleted one.
 */
export function getAppointmentsByPatient(
  patientId: number,
  includeDeleted = false,
): Promise<Appointment[]> {
  return apiClient
    .get<ApiResponse<Appointment[]>>(`/patients/${patientId}/appointments`, {
      params: includeDeleted ? { includeDeleted: 1 } : undefined,
    })
    .then((r) => r.data.data);
}

// ─── Block Time ───────────────────────────────────────────────────────────────

/**
 * Block availability flag — mirrors legacy `scheduled_appointment_recur.blockType`
 * (`scheduler_block_details.php` L265-L275): 1 = Available Time, 2 = Not Available.
 */
export type BlockAvailability = 1 | 2;

/**
 * Recurrence timeframe scope — mirrors legacy `timeframeType` radios
 * (`scheduler_block_details.php` L545-L561):
 *   1 = No end date, 2 = For N days, 3 = Through date.
 */
export type BlockTimeframeType = 1 | 2 | 3;

export interface BlockDayOfWeekFlags {
  mon?: 0 | 1;
  tue?: 0 | 1;
  wed?: 0 | 1;
  thu?: 0 | 1;
  fri?: 0 | 1;
  sat?: 0 | 1;
  sun?: 0 | 1;
}

export interface CreateBlockTimePayload {
  title: string;
  /** Naive wall-clock datetime in the facility/account timezone, format
   *  `"YYYY-MM-DD HH:MM:SS"`. The backend's
   *  `SchedulerWorkflowService::normalizeDateTime` parses it via
   *  `Carbon::parse($value, $sourceTz)` where `$sourceTz` is
   *  `facilities.timezoneID -> aa_accounts.timezone`. Do NOT send an ISO
   *  `Z`-suffixed string — Carbon honours the suffix regardless of
   *  `$sourceTz` and the value will be persisted at the wrong instant.
   *  Mirrors `UnifiedAppointmentModal`'s save format. */
  startTime: string;
  /** See `startTime`. */
  endTime: string;
  duration?: number;
  date?: string;
  resourceIds: number[];
  facilityId?: number;
  appointmentTypeId?: number;
  appointmentTypeGroupId?: number;
  backgroundColor?: string;
  blockAvailability?: BlockAvailability;
  required?: 0 | 1 | boolean;
  notes?: string;
  recurring?: 0 | 1 | boolean;
  timeframeType?: BlockTimeframeType;
  timeframeDuration?: number;
  timeframeDate?: string;
  daysOfWeek?: BlockDayOfWeekFlags | string[];
  extraDates?: string[];
  exceptionDates?: string[];
}

export interface BlockTimeRecurrence {
  recurring: boolean;
  active: 0 | 1;
  blockType: BlockAvailability;
  blockTypeLabel: 'available' | 'unavailable';
  required: boolean;
  appointmentTypeGroupId: number;
  timeframeType: BlockTimeframeType;
  timeframeDuration: number;
  timeframeDate: string | null;
  recurringEndDate: string | null;
  recurringPattern: string | null;
  daysOfWeek: string[];
  mon: 0 | 1;
  tue: 0 | 1;
  wed: 0 | 1;
  thu: 0 | 1;
  fri: 0 | 1;
  sat: 0 | 1;
  sun: 0 | 1;
  extraDates: string[];
  exceptionDates: string[];
}

export interface BlockTime {
  blockTimeId: number;
  title: string;
  startTime: string;
  endTime: string;
  duration: number;
  resourceId: number | null;
  resourceIds: number[];
  facilityId: number | null;
  appointmentTypeId: number | null;
  appointmentTypeGroupId: number | null;
  backgroundColor: string | null;
  fontColor: string | null;
  blockType: 'available' | 'unavailable';
  blockAvailability: BlockAvailability;
  required: boolean;
  notes: string | null;
  recurring: boolean;
  recurringPattern: string | null;
  recurringEndDate: string | null;
  daysOfWeek: string[];
  extraDates: string[];
  exceptionDates: string[];
  recurrence: BlockTimeRecurrence;
  accountId: number;
  auditRecordId: string | null;
}

export interface BlockTimeShowResponse {
  block: BlockTime;
  recurrence: BlockTimeRecurrence | null;
  allowances: string[];
  exceptions: string[];
}

export interface BlockHistoryEntry {
  blockId: number;
  auditRecordId: string;
  name: string | null;
  startTime: string | null;
  endTime: string | null;
  reason: string;
  appointmentTypeId: number;
  facilityId: number;
  auditStartDate: string | null;
  auditEndDate: string | null;
  auditChangeReason: string;
  userId: number;
  userFullName: string | null;
  userName: string | null;
}

export interface BlockToAppointmentSeed {
  seedFromBlockId: number;
  seed: {
    title: string;
    startTime: string | null;
    endTime: string | null;
    duration: number;
    facilityId: number;
    appointmentTypeId: number;
    appointmentTypeGroupId: number;
    resourceIds: number[];
    notes: string;
    patientId: number;
    planId: number;
  };
}

/** Create a block-time entry on the calendar. */
export function createBlockTime(data: CreateBlockTimePayload): Promise<BlockTime> {
  return apiClient
    .post<ApiResponse<BlockTime>>('/appointments/block-time', data)
    .then((r) => r.data.data);
}

/**
 * Fetch a single block plus its recurrence + allowances/exceptions.
 * Mirrors backend `AppointmentBlocksController::show` payload shape.
 */
export function getBlockTime(id: number): Promise<BlockTimeShowResponse> {
  return apiClient
    .get<ApiResponse<BlockTimeShowResponse>>(`/appointment-blocks/${id}`)
    .then((r) => r.data.data);
}

/** Update an existing block series (mirrors legacy scheduler_block_details.php save). */
export function updateBlockTime(id: number, data: Partial<CreateBlockTimePayload>): Promise<BlockTime> {
  return apiClient
    .put<ApiResponse<BlockTime>>(`/appointment-blocks/${id}`, data)
    .then((r) => r.data.data);
}

/** Delete an entire block series (soft delete + recurrence end). */
export function deleteBlockTime(id: number): Promise<void> {
  return apiClient
    .delete<ApiResponse<void>>(`/appointment-blocks/${id}`)
    .then(() => undefined);
}

/**
 * Add a per-occurrence exception to a recurring block
 * (legacy "Delete this occurrence"). Backend route is
 * `DELETE /appointment-blocks/{id}/occurrence?date=YYYY-MM-DD`.
 */
export function deleteBlockOccurrence(id: number, date: string): Promise<void> {
  return apiClient
    .delete<ApiResponse<void>>(`/appointment-blocks/${id}/occurrence`, { params: { date } })
    .then(() => undefined);
}

/**
 * Split a specific occurrence out of a recurring block into a new
 * single-date block. Backend route is
 * `POST /appointment-blocks/{id}/convert-to-single`.
 */
export function convertBlockOccurrence(id: number, date: string): Promise<BlockTime> {
  return apiClient
    .post<ApiResponse<{ block: BlockTime; newBlockId: number; seriesBlockId: number }>>(
      `/appointment-blocks/${id}/convert-to-single`,
      { date }
    )
    .then((r) => r.data.data.block);
}

/** Audit history for a block (legacy scheduler_block_history.php). */
export function getBlockHistory(id: number): Promise<BlockHistoryEntry[]> {
  return apiClient
    .get<ApiResponse<BlockHistoryEntry[]>>(`/appointment-blocks/${id}/history`)
    .then((r) => r.data.data);
}

/**
 * Returns a seed payload for the appointment modal — mirrors legacy
 * `convertBlockToAppt` (calendar-test.php L6714-L6733). No DB write
 * happens here.
 */
export function convertBlockToAppointment(id: number): Promise<BlockToAppointmentSeed> {
  return apiClient
    .post<ApiResponse<BlockToAppointmentSeed>>(`/appointment-blocks/${id}/convert-to-appointment`)
    .then((r) => r.data.data);
}

/** Rich hover-popup payload for a block. See {@link BlockInfoPopup}. */
export function getBlockInfoPopup(id: number): Promise<BlockInfoPopup> {
  return apiClient
    .get<ApiResponse<BlockInfoPopup>>(`/appointment-blocks/${id}/info-popup`)
    .then((r) => r.data.data);
}

// ─── Scheduler dashboard endpoints ───────────────────────────────────────────

export interface PreviousDayCheckItem {
  appointmentId: number;
  patientId: number | null;
  patientName: string | null;
  appointmentTypeId: number | null;
  appointmentTypeName: string | null;
  resourceIds: number[];
  startTime: string;
  endTime: string;
  category: 'confirmed_not_checked_in' | 'checked_in_not_checked_back' | 'checked_in_not_checked_out' | 'waitlist_stranded';
  waitlistPosition?: number | null;
}

export interface PreviousDayCheckResponse {
  date: string;
  confirmedNotCheckedIn: PreviousDayCheckItem[];
  checkedInNotCheckedBack: PreviousDayCheckItem[];
  checkedInNotCheckedOut: PreviousDayCheckItem[];
  waitlistStranded: PreviousDayCheckItem[];
  totalCount: number;
}

/** Previous-day pending items (legacy scheduler_previous_day_check.php). */
export function getPreviousDayCheck(date?: string): Promise<PreviousDayCheckResponse> {
  const params: Record<string, string> = {};
  if (date) params.date = date;
  return apiClient
    .get<ApiResponse<PreviousDayCheckResponse>>('/scheduler/previous-day-check', { params })
    .then((r) => r.data.data);
}

/**
 * Legacy parity: `printOptionN` fields from calendar-test.php 6054-6165.
 * Each flag is an independent checkbox, NOT a layout enum.
 */
export interface PrintRangePrintOptions {
  printOption1?: boolean;  // patientNameSelect
  printOption2?: boolean;  // patientIDSelect
  printOption3?: boolean;  // chartIDSelect
  printOption4?: boolean;  // planIDSelect
  printOption5?: boolean;  // phoneSelect
  printOption6?: boolean;  // appointmentNameSelect
  printOption7?: boolean;  // appointmentTypeSelect
  printOption8?: boolean;  // facilityNameSelect
  printOption9?: boolean;  // apptNotesSelect
  printOption10?: boolean; // resourceDisplayListSelect
  printOption11?: boolean; // birthDateSelect
  printOption12?: boolean; // heightWeightSelect
  printOption13?: boolean; // bmiSelect
  printOption14?: boolean; // appliedFiltersSelect
  printOption15?: boolean; // patientDataPages
  printOption16?: boolean; // landscape (printOrientation == 1)
  printOption17?: boolean; // allergiesSelect
  printOption18?: boolean; // languageSelect
  printOption19?: boolean; // proceduresSelect
  printOption20?: boolean; // implantsSelect
  printOption21?: boolean; // labChecklistSelect
}

export interface PrintRangePayload {
  startDate: string;
  endDate: string;
  /** 21 independent legacy print flags. */
  printOptions?: PrintRangePrintOptions;
  facilityIds?: number[];
  appointmentTypeIds?: number[];
  resourceIds?: number[];
  includeCancelled?: boolean;
  includeBlocks?: boolean;
}

/** Print a calendar date range (HTML payload). */
export function printCalendarRange(payload: PrintRangePayload): Promise<{ html: string }> {
  return apiClient
    .post<ApiResponse<{ html: string }>>('/scheduler/print/range', payload)
    .then((r) => r.data.data);
}

/** Print the waitlist (HTML payload). */
export function printWaitlist(payload: {
  date?: string;
  facilityIds?: number[];
  appointmentTypeIds?: number[];
  resourceIds?: number[];
} = {}): Promise<{ html: string }> {
  return apiClient
    .post<ApiResponse<{ html: string }>>('/scheduler/print/waitlist', payload)
    .then((r) => r.data.data);
}

/** P1-12: print-scope options for the print modal (legacy scheduler_document_list.php). */
export interface SchedulerPrintPacketOption {
  id: number;
  name: string;
  kind: 'packet';
}
export interface SchedulerPrintDocumentOption {
  id: number;
  title: string;
  categoryId: number;
  categoryName: string;
  kind: 'document';
}
export interface SchedulerPrintDocumentOptions {
  packets: SchedulerPrintPacketOption[];
  documents: SchedulerPrintDocumentOption[];
}

export function getSchedulerPrintDocumentOptions(): Promise<SchedulerPrintDocumentOptions> {
  return apiClient
    .get<ApiResponse<SchedulerPrintDocumentOptions>>('/scheduler/print/documents')
    .then((r) => r.data.data);
}

export interface PrintDocumentPacketPayload {
  kind: 'packet' | 'document';
  id: number;
  appointmentIds: number[];
}

export function printDocumentPacket(payload: PrintDocumentPacketPayload): Promise<{
  kind: 'packet' | 'document';
  id: number;
  title: string;
  documents: Array<{ id: number; title: string }>;
  appointments: CalendarAppointment[];
  html: string;
}> {
  return apiClient
    .post<ApiResponse<{
      kind: 'packet' | 'document';
      id: number;
      title: string;
      documents: Array<{ id: number; title: string }>;
      appointments: CalendarAppointment[];
      html: string;
    }>>('/scheduler/print/packet', payload)
    .then((r) => r.data.data);
}

/** Legacy aa_accounts status color palette + gate flag (scheduler-display-week-cell.php 57-66). */
export interface StatusColorPalette {
  scheduled: string | null;
  confirmed: string | null;
  checkedIn: string | null;
  checkedBack: string | null;
  checkedOut: string | null;
  noShow: string | null;
  waitList: string | null;
  cancelled: string | null;
  contactAttempted: string | null;
}

export interface CalendarSettings {
  showPatientName: boolean;
  showPatientTags: boolean;
  showPatientPhoto: boolean;
  showPatientDOB: boolean;
  showPatientPhone: boolean;
  showPatientChartID: boolean;
  showPlanStage: boolean;
  showWaits: boolean;
  showCoordinator: boolean;
  showMemberships: boolean;
  showLanguage: boolean;
  showProcedures: boolean;
  showNotes: boolean;
  showResourceName: boolean;
  showAppointmentType: boolean;
  showStatusBadges: boolean;
  showColorFlags: boolean;
  use24HourFormat: boolean;
  /** aa_accounts.statusColors: when true, cards use the palette below for the
   *  border color following status() precedence. */
  statusColors?: boolean;
  schedulerGradients?: boolean;
  schedulerResources?: boolean;
  schedulerFontSize?: string | null;
  /** aa_accounts.timezone: legacy sentinel 10 hides the now-line (calendar-test.php 2428). */
  accountTimezoneId?: number | null;
  /** IANA timezone for the account (e.g. 'America/New_York'), resolved from
   *  aa_accounts.timezone -> country_tzones.tzName. Used as the default display
   *  timezone before a facility is selected. */
  accountIanaTimezone?: string | null;
  statusColorPalette?: StatusColorPalette;
  /** aa_accounts_calendar.displayDailyAlert: gates the previous-day banner
   *  auto-run (calendar-test.php 1098-1123). */
  displayDailyAlert?: boolean;
  /** aa_accounts.draggableAppts: when true, the scheduler grid allows
   *  click/drag time-shift + bottom-edge resize on appointment cards
   *  (legacy calendar-test.php L2597 / makeApptDraggable L2729). */
  draggableAppts?: boolean;
  [key: string]: unknown;
}

/** Account-level aa_accounts_calendar field-visibility flags. */
export function getCalendarSettings(): Promise<CalendarSettings> {
  return apiClient
    .get<ApiResponse<CalendarSettings>>('/scheduler/calendar-settings')
    .then((r) => r.data.data);
}

export interface AppointmentListStats {
  total: number;
  completed: number;
  noShow: number;
  cancelled: number;
  actionRequired: number;
}

export function getAppointmentStats(filters?: AppointmentFilters): Promise<AppointmentListStats> {
  const params: Record<string, string | undefined> = {};
  if (filters?.startDate) params.dateFrom = filters.startDate;
  if (filters?.endDate) params.dateTo = filters.endDate;
  if (filters?.facilityIds?.length) params.facilityIds = filters.facilityIds.join(',');
  if (filters?.providerIds?.length) params.providerIds = filters.providerIds.join(',');
  if (filters?.typeIds?.length) params.appointmentTypeIds = filters.typeIds.join(',');
  if (filters?.firstAppointment) params.firstAppointment = '1';
  if (filters?.compAppointment) params.compAppointment = '1';
  return apiClient
    .get<{ data: AppointmentListStats }>('/appointments/stats', { params })
    .then((r) => r.data.data);
}
